import { EventEmitter, Injectable } from "@angular/core";
import { Action, Store, select } from "@ngrx/store";
import { catchError, map, take, tap, throwError } from "rxjs";
import { CustomerActions, CustomerSelectors } from "../store";

import { FormGroup } from "@angular/forms";
import { AuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { Entity } from "@app/entity/model/entity";
import { Project } from "@app/project/model";
import { ProjectDbService } from "@app/project/services/project-db.service";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { RouterActions } from "@app/root-store";
import { SnackbarActions } from "@app/root-store/actions";
import { LoadingState } from "@app/root-store/api-result.state";
import { RouterDispatchers } from "@app/root-store/dispatchers";
import { CommonModelService } from "@app/shared/services/common-model.service";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { User } from "@app/user/model/user";
import { UserManagementActions } from "@app/user/store";
import { UserManagementState } from "@app/user/store/user-management.state";
import { switchMap } from "rxjs/operators";
import { customerRoutePaths } from "../customer.routes";
import { Customer } from "../model/customer";

@Injectable({
    providedIn: "root",
})
export class CustomerService extends CommonModelService {
    /**
     * Methods in CommonModelService:
     *
     * dispatchAndSelectUserRoles
     * dispatchUserRoles
     * selectAuthEntityIdAndDispatchEntityUsers
     */

    /**
     * Selector methods
     */
    selectCurrentCustomer() {
        return this.store.pipe(
            select(CustomerSelectors.getCustomerState),
            tap(state => {
                if (state === LoadingState.init) {
                    this.store.dispatch(UserManagementActions.UserActions.loadAllEntityCustomers(null));
                }
            }),
            switchMap(() =>
                this.store.pipe(
                    select(CustomerSelectors.getCurrentCustomer),
                    map(customer => customer)
                )
            )
        );
    }
    selectCustomerList() {
        return this.store.pipe(select(CustomerSelectors.getCustomerList));
    }

    selectMobileCustomerList() {
        return this.store.pipe(select(CustomerSelectors.getMobileCustomerList));
    }

    selectCustomerCallState() {
        return this.store.pipe(select(CustomerSelectors.getCustomerCallState));
    }

    generateCustomerInviteCode() {
        return Math.random().toString(36).substr(2, 9); // NOSONAR
    }

    /**
     * getUpsertAction
     *
     * @param customer The customer data to upsert
     * @param callbackEmitter The emitter to call when the action success
     */
    getUpsertAction(customer: Customer, callbackEmitter: EventEmitter<Action>, logData: AuditLogInput = null) {
        if (!customer.inviteCode) {
            customer.inviteCode = this.generateCustomerInviteCode();
        }
        if (customer.id) {
            return CustomerActions.update({ customer, callbackEmitter, logData });
        }
        return CustomerActions.create({ customer, callbackEmitter, logData });
    }

    /**
     * getUpsertFailureAction
     *
     * @param error The error to pass to the action
     */
    getUpsertFailureAction(error: DataServiceError<Customer>) {
        if (error.requestData.id) {
            return CustomerActions.updateFailure({ error });
        }
        return CustomerActions.createFailure({ error });
    }

    /**
     * upsertCustomer
     *
     * @param form The form being validated and submitted
     * @param emitter The event emitter to handle a successful submission
     * @param customer The customer to upsert
     */
    upsertCustomer(
        form: FormGroup,
        emitter: EventEmitter<CustomerActions.CustomerActions | RouterActions.RouterGo>,
        customer: Customer = {} as Customer,
        logInput: AuditLogInput = null
    ) {
        const submissionData = { ...customer, ...form.getRawValue() };
        let action: CustomerActions.CustomerActions = this.getUpsertAction(submissionData, emitter, logInput);

        if (form.invalid) {
            const error = new DataServiceError<Customer>("Invalid submission", submissionData);
            action = this.getUpsertFailureAction(error);
            emitter.emit(action);
        }

        if (this.isUpsertCustomerAction(action) && logInput) {
            action.logData = logInput;
        }

        this.store.dispatch(action);
    }

    private isUpsertCustomerAction(action: CustomerActions.CustomerActions): action is CustomerActions.CustomerAction {
        return action.type === CustomerActions.update.type || action.type === CustomerActions.create.type;
    }

    ofType(action: Action, types: string[] = []) {
        return types.some(type => type === action.type);
    }

    /**
     * subscribeToEmitter
     *
     * @param emitter The emitter to handle a cancel or success action
     */
    subscribeToEmitter(emitter: EventEmitter<CustomerActions.CustomerActions | RouterActions.RouterGo>) {
        // TODO: Fix this reference
        if (!emitter.observers.length) {
            const emitterSubscription = emitter.subscribe(action => {
                if (
                    this.ofType(action, [
                        RouterActions.routerGo.type,
                        CustomerActions.createSuccess.type,
                        CustomerActions.updateSuccess.type,
                        CustomerActions.deleteSuccess.type,
                    ])
                ) {
                    this.store.dispatch(action);
                    this.cancelAction(true);
                    emitterSubscription.unsubscribe();
                }
            });
        }
    }

    /**
     * DISPATCHERS
     * Dispatchers used to keep the store dependency out of component classes
     *
     */

    /**
     * deleteCustomer
     *
     * @param customer The customer to delete
     */
    deleteCustomer(customer: Customer, logData?: AuditLogInput) {
        this.store.dispatch(CustomerActions.deleteConfirmation({ customer, logData }));
    }

    /**
     * deleteAction
     *
     * @param customer The customer to delete
     * @param callbackEmitter The emitter to be emitted with the success action
     */
    deleteAction(customer: Customer, callbackEmitter: EventEmitter<any>, logData?: AuditLogInput) {
        this.store.dispatch(CustomerActions.deleteItem({ customer, callbackEmitter, logData }));
    }

    /**
     * cancelAction
     *
     * @param dispatch boolean to decide whether to dispatch the cancel route action or just return it
     */
    cancelAction(dispatch = false) {
        return this.routerDispatchers.goToList(customerRoutePaths, dispatch);
    }

    resendInvite(customer: Customer) {
        this.store.dispatch(
            CustomerActions.sendCustomerWelcomeEmail({
                recipientEmail: customer.email,
                recipientFirstName: customer.firstName,
                recipientLastName: customer.lastName,
                inviteCode: customer.inviteCode,
            })
        );
    }

    onboardCustomer(project: Project, primaryUser: User, entity: Entity, eventEmitter?: EventEmitter<boolean>) {
        if (project?.claresystem) {
            this.assignUserToClareSystem(primaryUser, project, entity, eventEmitter);
        } else {
            this.projectDispatchers.dispatch(
                SnackbarActions.openSnackbar({
                    message: new HttpResponseHandler(null, null, "Please add a controller before inviting your customer"),
                })
            );
        }
    }

    assignUserToClareSystem(primaryUser: User, project: Project, entity: Entity, eventEmitter?: EventEmitter<boolean>) {
        this.projectDbService
            .connectUserToClaresystem(primaryUser, project.claresystem)
            .pipe(
                take(1),
                tap(_ => {
                    eventEmitter?.emit(true);
                    if (!primaryUser.gitkitId) {
                        this.updateInviteCode(primaryUser);
                    } else {
                        this.sendCustomerMovedInEmail(primaryUser, entity);
                    }
                    this.projectDispatchers.dispatchProjectUsers(project.id);
                }),
                catchError(error => {
                    this.projectDispatchers.dispatch(
                        SnackbarActions.openSnackbar({
                            message: new HttpResponseHandler(null, null, "Update failed!", true),
                        })
                    );
                    eventEmitter?.emit(false);
                    return throwError(() => error);
                })
            )
            .subscribe();
    }

    sendCustomerMovedInEmail(customer: Customer, entity: Entity, companyAdmins?: User[]) {
        const payload = {
            user: {
                email: customer.email,
                firstName: customer.firstName,
                lastName: customer.lastName,
            },
            entityName: entity?.name,
            companyAdmins,
        };

        this.store.dispatch(CustomerActions.sendCustomerMovedInEmail(payload));
    }

    updateInviteCode(primaryUser: User) {
        primaryUser.inviteCode = this.generateCustomerInviteCode();
        this.projectDispatchers.dispatch(CustomerActions.updateInviteCode({ customer: primaryUser }));
    }

    /**
     * goToAddPage
     *
     */
    goToAddPage() {
        this.routerDispatchers.goToAdd(customerRoutePaths);
    }

    /**
     *
     * @param id The ID of the customer to edit
     */
    goToEditPage(id?: number) {
        this.routerDispatchers.goToEdit(customerRoutePaths, id);
    }

    constructor(
        private readonly routerDispatchers: RouterDispatchers,
        readonly store: Store<UserManagementState>,
        private readonly projectDispatchers: ProjectDispatchers,
        private readonly projectDbService: ProjectDbService
    ) {
        super(store);
    }
}
